// @ts-strict-ignore
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { RangeOnLineArticleData } from '@project/components/article/model/range-on-line-article-data';
import { BlockingZoneArticleManager } from '@project/components/article/manager/blocking-zone-article-manager';
import { ProjectModel } from '@project/model/project.model';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { PasteArticleInformation } from '@project/components/article/model/paste-article-information';
import { CustomArticleInfoVO } from '@project/components/article/model/vo/custom-article.vo';
import { ArticleByTypeManager } from '@article/manager/article-by-type.manager';
import { CustomArticleManager } from '@project/components/article/custom-article/custom-article.manager';
import { Log } from '@shared/debug/log';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { BlockingZoneArticleManagerFactory } from '../manager/blocking-zone-article-manager.factory';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { InsertArticleService } from '../service/insert-article.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { SelectionModelService } from '../service/selection-model.service';
import { PasteArticleManager } from '../manager/paste-article.manager';
import { BuildingBlockPasteArticleManager } from '../manager/building-block-paste-article.manager';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { AlertHelper } from '@shared/alert/alert.helper';

export class PasteArticlePresenter {
  public static readonly PASTE_AT_START: number = 1;
  public static readonly PASTE_AFTER_LAST_ARTICLE_ON_RAIL: number = 2;
  public static readonly PASTE_AFTER_EACH_SELECTED_ARTICLE: number = 3;
  public static readonly PASTE_AFTER_LAST_ARTICLE_BEFORE_SELECTION: number = 4;

  public static readonly LANGUAGE_KEY_CARRIER_ARTICLE_NOT_FOUND_MESSAGE: string = 'pasteArticlePopup.error.message.carrier_article_not_found';
  public static readonly LANGUAGE_KEY_CARRIER_ARTICLE_NOT_FOUND_TITLE: string = 'pasteArticlePopup.error.title.carrier_article_not_found';

  public articlesOnRailExist: boolean;
  public selectedArticlesOnRailExist: boolean;
  public lastArticleBeforeSelectionExists: boolean;
  public rangeOnLineArticleType: string;
  public initialized: boolean = true;
  public displayErrors: boolean = true;

  private _rangeOnLineArticleDatas: RangeOnLineArticleData[];
  private _articleToInsert: ArticleVO;
  private _isPasteOperation: boolean = true;
  private _customArticleInfo: CustomArticleInfoVO;
  private _errorMessageAlreadyDisplayed: boolean = false;
  private _articleTypeId: number;

  constructor(
    private insertArticleUtils: InsertArticleUtils,
    private alertHelper: AlertHelper,
    private projectModel: ProjectModel,
    private projectArticleService: ProjectArticleService,
    private mountingRailHelper: MountingRailHelper,
    private pasteArticleManager: PasteArticleManager,
    private buildingBlockPasteArticleManager: BuildingBlockPasteArticleManager,
    private articleByTypeManager: ArticleByTypeManager,
    private selectionModelService: SelectionModelService,
    private undoRedoHelper: UndoRedoHelper,
    private blockingZoneArticleManagerFactory: BlockingZoneArticleManagerFactory,
    private containerService: ContainerService,
    private undoRedoUtils: UndoRedoUtils,
    private insertArticleHelperFactory: InsertArticleHelperFactory,
    private _insertArticleService: InsertArticleService,
    private _applicationRefreshService: ApplicationRefreshService,
    private _detachArticleUsageService: DetachArticleUsageService
  ) {}

  public set articleToInsert(articleToInsert: ArticleVO) {
    this._articleToInsert = articleToInsert;
  }

  public get articleToInsert(): ArticleVO {
    return this._articleToInsert;
  }

  public set articleTypeId(articleTypeId: number) {
    this._articleTypeId = articleTypeId;
    this.isPasteOperation = false;
    this.articleByTypeManager.retrieveArticleHeadByType(articleTypeId);
  }

  public get articleTypeId(): number {
    return this._articleTypeId;
  }

  public set customArticleInfo(customInfo: CustomArticleInfoVO) {
    this._customArticleInfo = customInfo;
  }

  public get customArticleInfo(): CustomArticleInfoVO {
    return this._customArticleInfo;
  }

  public get isPasteOperation(): boolean {
    return this._isPasteOperation;
  }

  public set isPasteOperation(value: boolean) {
    this._isPasteOperation = value;
    this.rangeOnLineArticleType = this._isPasteOperation
      ? this.currentManager.getCopiedRangeOnLineArticleType()
      : ArticleTypeConstants.TYPE_MOUNTING_RAIL;
    this.initRangeOnLineArticles(this.rangeOnLineArticleType);
  }

  public isBuildingBlockPaste: boolean = false;

  private get currentManager(): PasteArticleManager {
    return this.isBuildingBlockPaste ? this.buildingBlockPasteArticleManager : this.pasteArticleManager;
  }

  public async executePasteOperation(
    pasteOperation: number,
    offset: number,
    length: number = NaN,
    isLeadingValue: boolean = false,
    repeatCount: number = 1
  ): Promise<void> {
    const rangeOnLineDatas: RangeOnLineArticleData[] = this.filterRangeOnLineDataByOperation(pasteOperation, this.isBuildingBlockPaste);
    if (this._isPasteOperation) {
      await this.pasteCopiedArticles(rangeOnLineDatas, pasteOperation, offset, repeatCount);
    } else {
      await this.insertArticleOnRail(rangeOnLineDatas, pasteOperation, offset, length, isLeadingValue);
    }
  }

  public async pasteOperationCompletedHandler(articlesPasted: boolean): Promise<void> {
    if (!articlesPasted) {
      const errorMessage = this.currentManager.getFirstPasteError();
      if (errorMessage) {
        this.displayErrorMessage(errorMessage.message, errorMessage.title, null, false);
      }
    }
    await this.currentManager.finalizePasteOperation();
  }

  private initRangeOnLineArticles(rangeOnLineArticleType: string): void {
    this.storeRangeOnLineArticleDatas(rangeOnLineArticleType);
    if (ArrayUtils.isEmpty(this._rangeOnLineArticleDatas)) {
      this.displayErrorMessage(
        PasteArticlePresenter.LANGUAGE_KEY_CARRIER_ARTICLE_NOT_FOUND_MESSAGE,
        PasteArticlePresenter.LANGUAGE_KEY_CARRIER_ARTICLE_NOT_FOUND_TITLE
      );
    }
  }

  private storeRangeOnLineArticleDatas(rangeOnLineArticleType: string): void {
    if (!rangeOnLineArticleType) {
      return;
    }
    this._rangeOnLineArticleDatas = this.mountingRailHelper.createRangeOnLineArticleDataBySelection(rangeOnLineArticleType);
    this.updateRangeOnLineArticleDatas();
  }

  private async insertArticleOnRail(rangeOnLineDatas: RangeOnLineArticleData[],
                              pasteOperation: number,
                              offset: number,
                              articleLength: number,
                              isLeadingValue: boolean): Promise<void> {
    if (!this._articleToInsert ||
      ArrayUtils.isEmpty(rangeOnLineDatas) ||
      (isNaN(articleLength) && !ArticleUtils.isCustomArticle(this._articleToInsert))
    ) {
      return;
    }
    const rangeOnLineDataToProcess: RangeOnLineArticleData = rangeOnLineDatas[0];
    const startArticleOffset: number = rangeOnLineDataToProcess.calculateStartOffset(pasteOperation, offset);
    if (ArticleUtils.isZoneArticle(this._articleToInsert)) {
      const blockingZoneManager: BlockingZoneArticleManager = this.blockingZoneArticleManagerFactory.getNewInstance();
      await blockingZoneManager.insertZoneArticle(
        this._articleToInsert,
        this.projectModel.getArticleUsage(rangeOnLineDataToProcess.baseUsageId),
        articleLength,
        startArticleOffset,
        true,
        isLeadingValue
      );
    } else if (ArticleUtils.isCustomArticle(this._articleToInsert)) {
      const customArticleManager: CustomArticleManager = new CustomArticleManager(
        this.insertArticleUtils,
        this.alertHelper,
        this.mountingRailHelper,
        this.projectArticleService,
        this.selectionModelService,
        this.undoRedoHelper,
        this.containerService,
        this.undoRedoUtils,
        this.insertArticleHelperFactory,
        this._insertArticleService,
        this._applicationRefreshService,
        this._detachArticleUsageService
      );
      await customArticleManager.insertCustomArticle(this._articleToInsert, this.customArticleInfo, startArticleOffset);
    } else {
      Log.error('Insert article operation with type: ' + this._articleToInsert.type.id + ' is not supported');
    }
  }

  private updateRangeOnLineArticleDatas(): void {
    for (const rangeOnLineData of this._rangeOnLineArticleDatas) {
      if (ArrayUtils.isNotEmpty(rangeOnLineData.articlesOnRail)) {
        this.articlesOnRailExist = true;
      }
      if (ArrayUtils.isNotEmpty(rangeOnLineData.selectedArticlesOnRail)) {
        this.selectedArticlesOnRailExist = true;
      }
      if (rangeOnLineData.lastArticleBeforeSelection) {
        this.lastArticleBeforeSelectionExists = true;
      }
    }
  }

  private filterRangeOnLineDataByOperation(pasteOperation: number, isBuildingBlock: boolean = false): RangeOnLineArticleData[] {
    const filteredDatas: RangeOnLineArticleData[] = [];
    for (const rangeOnLineData of this._rangeOnLineArticleDatas) {
      if (isBuildingBlock || rangeOnLineData.isOperationSupported(pasteOperation)) {
        filteredDatas.push(rangeOnLineData);
      }
    }
    return filteredDatas;
  }

  private async pasteCopiedArticles(rangeOnLineDatas: RangeOnLineArticleData[], pasteOperation: number, offset: number,
                              repeatCount: number): Promise<void> {
    this.currentManager.clearPasteOperationParameters();
    const pasteArticleInfos: PasteArticleInformation[] = [];
    if (pasteOperation === PasteArticlePresenter.PASTE_AFTER_EACH_SELECTED_ARTICLE) {
      const newRangeOnLineDatas: RangeOnLineArticleData[] = [];
      rangeOnLineDatas.forEach((rangeOnLineData) => {
        rangeOnLineData.selectedArticlesOnRail.forEach((ac) => {
          newRangeOnLineDatas.push(...this.mountingRailHelper.createRangeOnLineArticleDataByArticles([ac], this.rangeOnLineArticleType, true));
        });
      });
      if (newRangeOnLineDatas.length > 0) {
        rangeOnLineDatas = newRangeOnLineDatas;
      }
    }
    for (const rangeOnLineData of rangeOnLineDatas) {
      const startArticleOffset: number = rangeOnLineData.calculateStartOffset(
        pasteOperation,
        offset,
        this.currentManager.getCopiedOverlapInfo(),
        this.isBuildingBlockPaste
      );
      const pasteInfo: PasteArticleInformation = new PasteArticleInformation(rangeOnLineData.baseUsageId, startArticleOffset, repeatCount);
      pasteInfo.rangeOnLineData = rangeOnLineData;
      pasteInfo.offset = offset;
      pasteInfo.overlapInfo = this.currentManager.getCopiedOverlapInfo();
      pasteInfo.pasteOperation = pasteOperation;
      pasteInfo.isBuildingBlockPaste = this.isBuildingBlockPaste;
      pasteArticleInfos.push(pasteInfo);
    }
    await this.currentManager.pasteArticlesByPasteArticleInfo(pasteArticleInfos);
  }

  private displayErrorMessage(
    messageLocaleKey: string,
    titleLocaleKey: string,
    messageParameters: any = null,
    initializationCorrupted: boolean = true
  ): void {
    if (this._errorMessageAlreadyDisplayed || !this.displayErrors) {
      return;
    }

    this._errorMessageAlreadyDisplayed = true;
    if (initializationCorrupted) {
      this.initialized = false;
    }
    this.alertHelper.showError(titleLocaleKey, messageLocaleKey, messageParameters).then(() => {
      this._errorMessageAlreadyDisplayed = false;
    });
  }
}
